<script lang="ts" setup>
import type { ComputedRef, Ref } from "vue"
import type { ISelectItem } from "@/types"
import { autoUpdate, flip, useFloating } from "@floating-ui/vue"
import { onClickOutside } from "@vueuse/core"
import { useField } from "vee-validate"
import { computed, ref, toRef, watch } from "vue"
import { Button, Checkbox, Chip, Hint, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface ComboboxProps {
  name: string
  label: string
  items?: ISelectItem[]
  modelValue?: ISelectItem | ISelectItem[]
  clearable?: boolean
  error?: string
  disabled?: boolean
  hint?: string
  multiple?: boolean
  searchable?: boolean
}

const props = withDefaults(defineProps<ComboboxProps>(), {
  items: () => [],
  clearable: true,
  error: "",
  disabled: false,
  hint: "",
  multiple: false,
  searchable: false,
  modelValue: undefined,
})

const emit = defineEmits<{
  "update:modelValue": [value?: ISelectItem | ISelectItem[]]
  "update:search": [value: string]
}>()

const search = defineModel<string>("search", { default: "" })

const name = toRef(props, "name")

const { t } = useTranslation()

const selectRef: Ref<HTMLElement | null> = ref(null)
const selectInputRef: Ref<HTMLElement | null> = ref(null)
const selectItemsRef: Ref<HTMLElement | null> = ref(null)

const {
  value: field,
  errorMessage,
  handleChange,
  handleReset,
} = useField<ISelectItem | ISelectItem[] | undefined>(name, undefined, {
  initialValue: props.multiple ? [] : undefined,
})

const selectItemsVisible: Ref<boolean> = ref(false)

function toggleSelectListVisibility(): void {
  selectItemsVisible.value = !selectItemsVisible.value
}

onClickOutside(selectRef, () => {
  selectItemsVisible.value = false
})

const { floatingStyles } = useFloating(selectInputRef, selectItemsRef, {
  placement: "bottom-start",
  whileElementsMounted: autoUpdate,
  middleware: [flip({ crossAxis: false })],
})

function selectItem(item: ISelectItem) {
  if (item.disabled)
    return

  let currentValue = props.modelValue ?? field.value
  const currentIsArray = Array.isArray(currentValue)

  if (props.multiple && currentIsArray) {
    const currentValuesArray = currentValue as ISelectItem[]

    // check if new item already exists in currentValue
    const existingItemIndex = currentValuesArray.findIndex(i => i.value === item.value)

    if (existingItemIndex >= 0) {
      currentValue = currentValuesArray.toSpliced(existingItemIndex, 1)
    }
    else {
      // if it doesn't exist, lets push the new item into currentValue
      currentValuesArray.push(item)
      currentValue = currentValuesArray
    }

    // Finally, emit the performed updates
    emit("update:modelValue", currentValue)
    handleChange(currentValue)
  }
  else {
    emit("update:modelValue", item)
    handleChange(item)
  }

  // clear search on item selection
  if (props.searchable)
    emit("update:search", "")

  // Automatically close the select list if we're not allowing multi-selection
  if (!props.multiple)
    selectItemsVisible.value = false
}

function clearInput(): void {
  selectItemsVisible.value = false
  handleReset()
  emit("update:modelValue", props.multiple ? [] : { label: "", value: "" })
  emit("update:search", "")
}

const showClearable: ComputedRef<boolean> = computed(() => {
  const currentValue = props.modelValue ?? field.value

  return Boolean(props.clearable && (Array.isArray(currentValue) ? currentValue.length : currentValue?.value))
})

const selectedItemValues = computed(() => {
  const currentValue = props.modelValue ?? field.value
  if (Array.isArray(currentValue)) {
    return currentValue.map(i => i.value)
  }

  return currentValue?.value ? [currentValue.value] : []
})

const firstSelectedItem = computed<ISelectItem | undefined>(() => {
  const currentValue = props.modelValue ?? field.value

  if (!Array.isArray(currentValue))
    return currentValue

  return currentValue.length ? currentValue[0] : undefined
})

const selectionLength = computed(() => {
  const currentValue = props.modelValue ?? field.value

  if (!Array.isArray(currentValue))
    return currentValue?.value ? 1 : 0

  return currentValue?.length
})

watch(search, () => {
  if (!selectItemsVisible.value)
    toggleSelectListVisibility()
})
</script>

<template>
  <div>
    <div ref="selectRef" class="gpx-select-wrapper">
      <div
        class="gpx-input-container"
        :class="{
          'gpx-input-container_error': error || errorMessage,
        }"
      >
        <label class="gpx-input-label-container" :for="name">
          <input
            ref="selectInputRef"
            :name="name"
            class="gpx-input"
            :class="{
              'gpx-input_disabled': disabled,
              'gpx-select-input_open': selectItemsVisible,
              'gpx-select-input_no-label': !label,
            }"
            :value="search"
            :readonly="!searchable"
            @input="(e) => emit('update:search', (e.target as HTMLInputElement).value)"
            @keydown.enter.prevent
            @click="toggleSelectListVisibility"
            @keyup.space="toggleSelectListVisibility"
            @keyup.enter="toggleSelectListVisibility"
            @keyup.esc="selectItemsVisible && toggleSelectListVisibility()"
          >
          <span
            class="gpx-floating-label"
            :class="{
              'gpx-floating-label_is-floating': search,
              'gpx-floating-label_error': error || errorMessage,
            }"
          >
            {{ label }}
          </span>
        </label>
        <div v-if="firstSelectedItem" class="flex items-center gap-1.5">
          <Chip v-if="firstSelectedItem.label" class="text-sm">
            {{ firstSelectedItem.label }}
          </Chip>
          <Chip v-if="selectionLength > 1" class="text-sm whitespace-nowrap">
            + {{ selectionLength - 1 }} <span class="hidden md:inline">more</span>
          </Chip>
        </div>
        <Button
          v-if="!showClearable"
          variant="flat"
          type="button"
          data-test="toggle_button"
          @click="toggleSelectListVisibility"
        >
          <span class="sr-only">{{ t("clear_field", { label }) }}</span>
          <Icon
            icon="CircleChevronDown"
            class="gpx-select-icon"
            :style="`transform: rotate(${selectItemsVisible ? '180' : '0'}deg)`"
          />
        </Button>
        <Button
          v-if="showClearable"
          variant="flat"
          type="button"
          data-test="gpx-input-clear-button"
          @click="clearInput"
        >
          <span class="sr-only">{{ t("clear_field", { label }) }}</span>
          <Icon icon="Close" size="sm" />
        </Button>
      </div>
      <ul
        v-if="selectItemsVisible && items.length"
        ref="selectItemsRef"
        class="gpx-select-items rounded-sm z-20"
        :class="{ 'gpx-select-items_open': selectItemsVisible }"
        :style="floatingStyles"
      >
        <li
          v-for="(item, index) in items"
          :key="item.value"
          class="gpx-select-item"
          :class="{ 'gpx-select-item_disabled': item.disabled ?? false }"
        >
          <button
            class="gpx-select-item-button flex items-center"
            :disabled="item.disabled"
            @click.prevent="() => selectItem(item)"
            @focusout="index + 1 === items.length && (selectItemsVisible = false)"
          >
            <Checkbox
              :name="item.value"
              :label="item.label"
              :model-value="selectedItemValues.includes(item.value)"
              readonly
            />
          </button>
        </li>
      </ul>
      <div v-else-if="selectItemsVisible" class="flex">
        <div class="gpx-select-items gpx-select-items_empty">
          <slot name="empty">
            <p>No data available</p>
          </slot>
        </div>
      </div>
    </div>
    <span v-if="error || errorMessage" class="gpx-field-error">{{ error || errorMessage }}</span>
    <Hint v-if="hint" :hint="hint" />
  </div>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css"

.gpx-select-input_no-label {
  padding: 0 1rem;
}

.gpx-select-wrapper {
  position: relative;
}

.gpx-floating-label {
  pointer-events: none;
}

.gpx-select-items {
  @apply w-full bg-white border border-gray-300 overflow-y-auto max-h-80 py-1;
}

.gpx-select-items_empty {
  @apply flex py-6 px-4 justify-center items-center text-sm font-medium text-gray-700 rounded-lg;
}

.gpx-select-items_open {
  border-top: none;
}

.gpx-select-item-button {
  background-color: transparent;
  border: none;
  width: 100%;
  height: 100%;
  padding: 0.5rem;
  transition: background-color 0.1s ease;
  text-align: start;
}

.gpx-select-item-button:hover {
  @apply bg-gray-100 cursor-pointer;
}

.gpx-select-item-button:disabled {
  background-color: transparent;
  cursor: not-allowed;
}

.gpx-select-item_disabled {
  @apply text-gray-500 cursor-not-allowed;
}

.gpx-select-icon {
  margin-right: -0.3rem;
  transition: transform 0.2s ease;
}
</style>
